import React, { useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericTabs, { TabOption } from "@ui-components/Tabs/GenericTabs";
import { Reflector } from "./ClusterModal";

interface ClusterStep2Props {
  reflectors: Reflector[];
  selectedReflectors: Reflector[];
  setSelectedReflectors: (val: Reflector[]) => void;
  onNext?: () => void;
  onBack: () => void;
  allSameRegion: boolean;
  allSameTier: boolean;
  selectedReflectorRegions: (string | undefined)[];
}

const STATUS_TABS: TabOption[] = [
  { label: "All", value: "All" },
  { label: "Online", value: "Online" },
  { label: "Offline", value: "Offline" },
];

const ClusterStep2: React.FC<ClusterStep2Props> = ({ reflectors, selectedReflectors, setSelectedReflectors, allSameRegion, allSameTier, selectedReflectorRegions }) => {
  const [regionFilter, setRegionFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("All");

  const filteredReflectors = reflectors.filter((r: Reflector) => {
    // Filter out reflectors that are already in a cluster
    const notInCluster = !r.clusterId && !r.cluster;
    
    // Apply region and status filters
    const regionMatch = !regionFilter || r.data?.region === regionFilter;
    const status = r.available ? "Online" : "Offline";
    const statusMatch = statusFilter === "All" || status === statusFilter;
    
    return notInCluster && regionMatch && statusMatch;
  });

  const toggleReflector = (id: string) => {
    if (selectedReflectors.some((r) => r.id === id)) {
      setSelectedReflectors(selectedReflectors.filter((r) => r.id !== id));
    } else if (selectedReflectors.length < 2) {
      const found = reflectors.find((r) => r.id === id);
      if (found) setSelectedReflectors([...selectedReflectors, found]);
    }
  };

  const uniqueRegions = Array.from(new Set(
    reflectors
      .filter(r => !r.clusterId && !r.cluster) // Only reflectors not in clusters
      .map((r) => r.data?.region)
      .filter(Boolean)
  ));
  const regionItems = uniqueRegions as string[];

  const hasFreeTierReflectors = filteredReflectors.some((r) => r.tier === "free");

  return (
    <div className="flex flex-col gap-6 pt-4">
      <h2 className=" font-semibold mb-2 text-semantic-text-primary text-body">Select two CTX Reflectors <span className="text-sm text-semantic-text-secondary">{`(${selectedReflectors.length}/2 selected)`}</span></h2>
      
      {hasFreeTierReflectors && (
        <div className="bg-semantic-bg-warning-primary border border-semantic-border-warning-primary rounded p-3">
          <div className="text-semantic-text-warning text-sm">
            <strong>Note:</strong> Free Tier reflectors cannot be used for cluster creation. Both instances in a cluster must be on the same paid tier.
          </div>
        </div>
      )}

      {/* Region validation error */}
      {selectedReflectors.length === 2 && !allSameRegion && (
        <div className="bg-semantic-bg-danger-primary border border-semantic-border-danger-primary rounded p-3">
          <div className="text-semantic-text-danger text-sm">
            <strong>Error:</strong> Selected reflectors must be in the same region. 
            Current regions: {selectedReflectorRegions.join(', ')}
          </div>
        </div>
      )}

      {/* Tier validation error */}
      {selectedReflectors.length === 2 && !allSameTier && (
        <div className="bg-semantic-bg-danger-primary border border-semantic-border-danger-primary rounded p-3">
          <div className="text-semantic-text-danger text-sm">
            <strong>Error:</strong> Selected reflectors must be on the same tier. 
            Current tiers: {selectedReflectors.map(r => r.tier || 'unknown').join(', ')}
          </div>
        </div>
      )}
      
      {/* Success message when valid selection */}
      {selectedReflectors.length === 2 && allSameRegion && allSameTier && (
        <div className="bg-semantic-bg-success-primary border border-semantic-border-success-primary rounded p-3">
          <div className="text-semantic-text-success text-sm">
            <strong>Valid selection:</strong> Both reflectors are in the same region ({selectedReflectorRegions[0]}) and tier ({selectedReflectors[0]?.tier || 'unknown'})
          </div>
        </div>
      )}
      <div className="flex flex-row gap-6 mb-2">
        <div className="flex-1">
          <div className="text-semantic-text-primary text-body mb-1">Filter by region</div>
          <GenericDropdown
            items={regionItems}
            value={regionFilter}
            onSelect={setRegionFilter}
            defaultLabel="Select one or more..."
          />
        </div>
        <div className="flex-1">
          <div className=" text-semantic-text-primary text-body mb-1">Filter by status</div>
          <GenericTabs
            tabs={STATUS_TABS}
            activeTab={statusFilter}
            onTabChange={setStatusFilter}
          />
        </div>
      </div>
      <GenericCard isCollapsible={false} className="px-0 py-2 ">
        {filteredReflectors.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-2">
              No reflectors available for cluster creation.
            </div>
            <div className="text-sm text-gray-400">
              Please ensure you have active reflectors configured and not already in a cluster.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredReflectors.map((r: Reflector) => {
              const isSelected = selectedReflectors.some((sel) => sel.id === r.id);
              const isFreeTier = r.tier === "free";
              const isDisabled = isFreeTier || (!isSelected && selectedReflectors.length >= 2);
              return (
                <GenericCard
                  key={r.id}
                  variant="primary"
                  isCollapsible={false}
                  hasBorder={true}
                  onClick={isFreeTier ? undefined : () => toggleReflector(r.id)}
                  bodyContent={
                    <div className={`relative w-full h-full ${isFreeTier ? 'opacity-60' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        className={`absolute top-2 right-4 w-5 h-5 z-10 border border-semantic-border-primary rounded checked:bg-semantic-bg-brand checked:border-semantic-bg-brand transition-colors ${isFreeTier ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        onClick={e => e.stopPropagation()}
                        onChange={isFreeTier ? undefined : () => toggleReflector(r.id)}
                      />
                      <div className="w-full">
                        <div className="flex flex-col gap-3 min-h-[120px]">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-lg text-semantic-text-primary">{r.name}</span>
                            {isFreeTier && <GenericTag label="Free Tier" variant="warning" />}
                          </div>
                          <div className="flex flex-col gap-3">
                            <div className="flex justify-between items-center">
                              <span className="text-body text-semantic-text-secondary font-medium">Region</span>
                              <span className="text-sm text-semantic-text-primary font-semibold">{r.data?.region || '-'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-body text-semantic-text-secondary font-medium">VPC ID</span>
                              <span className="text-sm text-semantic-text-primary font-semibold">{r.data?.vpc || '-'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-body text-semantic-text-secondary font-medium">Status</span>
                              <GenericTag label={r.available ? 'Online' : 'Offline'} variant={r.available ? 'success' : 'error'} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                >
                
                </GenericCard>
              );
            })}
          </div>
        )}
      </GenericCard>
      
    </div>
  );
};

export default ClusterStep2; 